/* Public directory SSE for Obchody (`/api/v1/public/directory/events`).
 *
 * Mirrors shop kiosk SSE transport (reconnect, connect timeout, health-check,
 * G10 storm latch pause/resume) but is platform-public: no tenant path,
 * no salesPointId, no entitlement probe.
 *
 * Forward each message as `websocket-message` CustomEvent so directory sync
 * can reuse the same realtime update contract as catalog.
 */

import { useEffect, useState } from 'react';
import {
  API_ENDPOINTS,
  normalizeRealtimeCatalogSseEventData,
} from 'pi-kiosk-shared';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import {
  getHttpStormRetryAfterMs,
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';

export const SSE_RECONNECT_DELAY_MS = 3_000;

/** Cap for reconnect backoff — avoids hammering Railway/proxy after repeated SSE drops. */
export const SSE_RECONNECT_MAX_DELAY_MS = 30_000;

const SSE_RECONNECT_BACKOFF_MULTIPLIER = 2;

/** Capped exponential backoff from base delay (3s → 6s → 12s → … → 30s). */
export function directorySseReconnectDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) {
    return SSE_RECONNECT_DELAY_MS;
  }
  const exponential =
    SSE_RECONNECT_DELAY_MS *
    SSE_RECONNECT_BACKOFF_MULTIPLIER ** (consecutiveFailures - 1);
  return Math.min(exponential, SSE_RECONNECT_MAX_DELAY_MS);
}

/** G8 — match shop/kiosk: abort if EventSource never reaches OPEN. */
export const SSE_CONNECT_TIMEOUT_MS = 10_000;

/**
 * G8 — periodic readyState probe while the hook believes the stream is live.
 * Catches silent death where some browsers leave EventSource without onerror.
 */
export const SSE_HEALTH_CHECK_INTERVAL_MS = 30_000;

export function usePublicDirectorySSE(): boolean {
  const { apiBaseUrl } = useRuntimeConfig();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const baseUrl = apiBaseUrl.replace(/\/+$/, '');
    const sseUrl = `${baseUrl}${API_ENDPOINTS.PUBLIC_DIRECTORY_EVENTS}`;

    let cancelled = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
    let healthCheckTimer: ReturnType<typeof setInterval> | null = null;
    let consecutiveFailures = 0;

    const clearReconnectTimer = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const clearConnectTimeout = (): void => {
      if (connectTimeoutTimer !== null) {
        clearTimeout(connectTimeoutTimer);
        connectTimeoutTimer = null;
      }
    };

    const clearHealthCheck = (): void => {
      if (healthCheckTimer !== null) {
        clearInterval(healthCheckTimer);
        healthCheckTimer = null;
      }
    };

    const closeEventSource = (): void => {
      clearConnectTimeout();
      clearHealthCheck();
      if (eventSource !== null) {
        eventSource.close();
        eventSource = null;
      }
      if (!cancelled) {
        setIsConnected(false);
      }
    };

    /** G10 — close live stream + cancel reconnect while latched (no stampede). */
    const pauseForStorm = (): void => {
      clearReconnectTimer();
      closeEventSource();
    };

    const scheduleReconnectAfterFailure = (): void => {
      if (cancelled) {
        return;
      }
      clearReconnectTimer();
      if (isHttpStormLatched()) {
        scheduleConnectAfterStorm();
        return;
      }
      consecutiveFailures += 1;
      const delayMs = directorySseReconnectDelayMs(consecutiveFailures);
      reconnectTimer = setTimeout(connect, delayMs);
    };

    const startHealthCheck = (): void => {
      clearHealthCheck();
      healthCheckTimer = setInterval(() => {
        if (cancelled || eventSource === null) {
          return;
        }
        if (isHttpStormLatched()) {
          pauseForStorm();
          scheduleConnectAfterStorm();
          return;
        }
        if (eventSource.readyState !== EventSource.OPEN) {
          clearHealthCheck();
          clearConnectTimeout();
          if (!cancelled) {
            setIsConnected(false);
          }
          eventSource.close();
          eventSource = null;
          scheduleReconnectAfterFailure();
        }
      }, SSE_HEALTH_CHECK_INTERVAL_MS);
    };

    const connect = (): void => {
      if (cancelled) {
        return;
      }
      if (isHttpStormLatched()) {
        scheduleConnectAfterStorm();
        return;
      }
      clearReconnectTimer();
      closeEventSource();
      eventSource = new EventSource(sseUrl);

      const markConnected = (): void => {
        if (cancelled || isHttpStormLatched()) {
          return;
        }
        consecutiveFailures = 0;
        clearConnectTimeout();
        setIsConnected(true);
        startHealthCheck();
      };

      connectTimeoutTimer = setTimeout(() => {
        connectTimeoutTimer = null;
        if (cancelled || eventSource === null) {
          return;
        }
        if (eventSource.readyState === EventSource.OPEN) {
          return;
        }
        clearHealthCheck();
        if (!cancelled) {
          setIsConnected(false);
        }
        eventSource.close();
        eventSource = null;
        scheduleReconnectAfterFailure();
      }, SSE_CONNECT_TIMEOUT_MS);

      eventSource.onopen = (): void => {
        markConnected();
      };
      eventSource.onmessage = (event): void => {
        markConnected();
        const normalized = normalizeRealtimeCatalogSseEventData(event.data);
        if (normalized.length === 0) {
          return;
        }
        window.dispatchEvent(
          new CustomEvent('websocket-message', {
            detail: { data: normalized },
          }),
        );
      };
      eventSource.onerror = (): void => {
        clearConnectTimeout();
        clearHealthCheck();
        if (!cancelled) {
          setIsConnected(false);
        }
        eventSource?.close();
        eventSource = null;
        if (cancelled) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        scheduleReconnectAfterFailure();
      };
    };

    const scheduleConnectAfterStorm = (): void => {
      if (cancelled) {
        return;
      }
      clearReconnectTimer();
      const waitMs = Math.max(getHttpStormRetryAfterMs(), 1);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (cancelled) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        connect();
      }, waitMs);
    };

    const tryStart = (): void => {
      if (cancelled) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      connect();
    };

    tryStart();

    const unsubscribeLatch = subscribeHttpStormLatch(() => {
      if (cancelled) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      if (eventSource === null) {
        clearReconnectTimer();
        connect();
      }
    });

    return (): void => {
      cancelled = true;
      unsubscribeLatch();
      setIsConnected(false);
      clearReconnectTimer();
      clearConnectTimeout();
      clearHealthCheck();
      eventSource?.close();
      eventSource = null;
    };
  }, [apiBaseUrl]);

  return isConnected;
}
